import type { HttpClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Button, Card, Input, Select, Switch, cn } from "@dl/web-core-js/ui";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { BadgeDollarSign, Boxes, Factory, Gauge, Hammer, Package, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  SALES_RANKING_BASES,
  SETTINGS_PATHS,
  fetchSingleRow,
  settingsClient,
  settingsKeys,
  sortBands,
  type CurrencyRateDTO,
  type GeneralSettingsDTO,
  type InventorySettingsDTO,
  type PricingSettingsDTO,
  type ProductSettingsDTO,
  type ProductionSettingsDTO,
  type PurchasingSettingsDTO,
  type RankBandDTO,
  type SalesRankingBasis,
  type ShopFloorSettingsDTO,
} from "../../api/settings";
import { SUPPLIER_PAYMENT_TERMS } from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { paymentTermsName } from "../purchasing/paymentTerms";
import { ImageTypeOrderGrid, ShopifyBundleImageRuleGrid, ShopifySingleItemImageRuleGrid } from "./imageSettingsGrids";
import { countsSummary, InventoryRuleJobDialog, useLastInventoryRuleRun } from "../InventoryRuleJobDialog";

/**
 * Every setting nexpim reads, on one page.
 *
 * Six single-row tables (general, products, inventory, production, purchasing, pricing) and two
 * short lists (rank bands, exchange rates) used to be spread over three
 * pages, two grids and a switch in a page header, with the rest hardcoded.
 * Here each value sits beside a sentence saying what reads it, grouped by the
 * domain that owns it, and the page saves as one: edit anything, then save
 * once. Only what changed is written.
 */

interface Draft {
  general: GeneralSettingsDTO;
  product: ProductSettingsDTO;
  inventory: InventorySettingsDTO;
  production: ProductionSettingsDTO;
  /** Null for an account without the purchasing view: the section is not shown and its row is never loaded. */
  purchasing: PurchasingSettingsDTO | null;
  /** Null for an account without the shop-floor setup view, like purchasing. */
  shopFloor: ShopFloorSettingsDTO | null;
  pricing: PricingSettingsDTO;
  bands: RankBandDTO[];
  rates: CurrencyRateDTO[];
}

type Errors = Partial<Record<string, string>>;

/** New bands get negative ids until saved, so rows keep a stable key and are told apart from stored ones. */
let nextTemporaryId = -1;

const SECTIONS = [
  { id: "general", icon: Gauge },
  { id: "products", icon: Package },
  { id: "inventory", icon: Boxes },
  { id: "production", icon: Factory },
  { id: "shopFloor", icon: Hammer },
  { id: "purchasing", icon: ShoppingCart },
  { id: "pricing", icon: BadgeDollarSign },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

export function SettingsPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.CONFIG_SETTINGS);
  // Purchasing is part of the hidden SIGM-replacement work (docs/sigm-mfi-replacement/dark-launch.md):
  // only accounts holding its view see, load or save that section.
  const showPurchasing = hasView(NexpimView.PURCHASING);
  // The shop floor's settings belong to the same hidden work; its setup view shows them.
  const showShopFloor = hasView(NexpimView.CONFIG_SHOP_FLOOR);
  const client = http as HttpClient;

  const [general, product, inventory, production, purchasing, pricing, bands, rates, shopFloor] = useQueries({
    queries: [
      { queryKey: settingsKeys.general, queryFn: () => fetchSingleRow<GeneralSettingsDTO>(client, SETTINGS_PATHS.general), enabled: allowed },
      { queryKey: settingsKeys.product, queryFn: () => fetchSingleRow<ProductSettingsDTO>(client, SETTINGS_PATHS.product), enabled: allowed },
      { queryKey: settingsKeys.inventory, queryFn: () => fetchSingleRow<InventorySettingsDTO>(client, SETTINGS_PATHS.inventory), enabled: allowed },
      { queryKey: settingsKeys.production, queryFn: () => fetchSingleRow<ProductionSettingsDTO>(client, SETTINGS_PATHS.production), enabled: allowed },
      { queryKey: settingsKeys.purchasing, queryFn: () => fetchSingleRow<PurchasingSettingsDTO>(client, SETTINGS_PATHS.purchasing), enabled: allowed && showPurchasing },
      { queryKey: settingsKeys.pricing, queryFn: () => fetchSingleRow<PricingSettingsDTO>(client, SETTINGS_PATHS.pricing), enabled: allowed },
      { queryKey: settingsKeys.rankBands, queryFn: () => settingsClient<RankBandDTO>(client, SETTINGS_PATHS.rankBands).findAll(), enabled: allowed },
      { queryKey: settingsKeys.rates, queryFn: () => settingsClient<CurrencyRateDTO>(client, SETTINGS_PATHS.rates).findAll(), enabled: allowed },
      { queryKey: settingsKeys.shopFloor, queryFn: () => fetchSingleRow<ShopFloorSettingsDTO>(client, SETTINGS_PATHS.shopFloor), enabled: allowed && showShopFloor },
    ],
  });

  const saved = useMemo<Draft | null>(() => {
    if (!general.data || !product.data || !inventory.data || !production.data || (showPurchasing && !purchasing.data) || !pricing.data || !bands.data || !rates.data) return null;
    if (showShopFloor && !shopFloor.data) return null;
    return {
      general: general.data,
      product: product.data,
      inventory: inventory.data,
      production: production.data,
      purchasing: showPurchasing ? purchasing.data ?? null : null,
      shopFloor: showShopFloor ? shopFloor.data ?? null : null,
      pricing: pricing.data,
      bands: sortBands(bands.data),
      rates: [...rates.data].sort((a, b) => a.currency.localeCompare(b.currency)),
    };
  }, [general.data, product.data, inventory.data, production.data, purchasing.data, shopFloor.data, pricing.data, bands.data, rates.data, showPurchasing, showShopFloor]);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  // The draft follows the server while it holds no edits, and after a save.
  // After a save that failed half-way, the rows that went through come back
  // from the server while the rest are still only in the draft; replacing the
  // draft then would throw away the edits that were refused, with no sign of it.
  // Bands are the exception once their step has started: a new band already
  // created would be created a second time from the draft, so they follow the server.
  const lastSaved = useRef<Draft | null>(null);
  const keepDraft = useRef<{ bandsFromServer: boolean } | null>(null);
  useEffect(() => {
    if (!saved) return;
    const previous = lastSaved.current;
    lastSaved.current = saved;
    const keep = keepDraft.current;
    setDraft((current) => {
      if (!current || !previous || !keep || JSON.stringify(current) === JSON.stringify(previous)) return saved;
      return keep.bandsFromServer ? { ...current, bands: saved.bands } : current;
    });
  }, [saved]);

  const errors = useMemo(() => (draft ? validate(draft, t) : {}), [draft, t]);
  const changes = useMemo(() => (draft && saved ? diff(saved, draft) : null), [draft, saved]);
  const changeCount = changes
    ? changes.rows.length + changes.bandsCreated.length + changes.bandsUpdated.length + changes.bandsDeleted.length + changes.rates.length
    : 0;
  const invalid = Object.keys(errors).length > 0;

  const failed = [general, product, inventory, production, ...(showPurchasing ? [purchasing] : []), ...(showShopFloor ? [shopFloor] : []), pricing, bands, rates].some(
    (query) => query.isError,
  );

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("settings.title", "Settings")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const save = async () => {
    if (!changes || invalid || saving) return;
    setSaving(true);
    keepDraft.current = null;
    let bandsStarted = false;
    try {
      for (const change of changes.rows) {
        await settingsClient(client, change.path).update(change.row);
      }
      const bandClient = settingsClient<RankBandDTO>(client, SETTINGS_PATHS.rankBands);
      bandsStarted = true;
      // Deletes first, then updates, then creates: a band can take over a
      // rank_to another one just gave up without tripping the unique index.
      for (const id of changes.bandsDeleted) await bandClient.remove(id);
      for (const band of changes.bandsUpdated) await bandClient.update(band);
      for (const band of changes.bandsCreated) await bandClient.create({ ...band, id: 0 });
      const rateClient = settingsClient<CurrencyRateDTO>(client, SETTINGS_PATHS.rates);
      for (const rate of changes.rates) await rateClient.update(rate);
      toast.success(t("settings.saved", "Settings saved"));
    } catch (error) {
      keepDraft.current = { bandsFromServer: bandsStarted };
      toast.error(error instanceof Error ? error.message : t("error.generic", "Something went wrong. Please try again."));
    } finally {
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t("settings.title", "Settings")}
        subtitle={t("settings.subtitle", "What a top seller is, how the plant plans, and what pricing starts from.")}
      />
      {failed ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : !draft ? (
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-faint">{t("common.loading", "Loading...")}</p>
        </div>
      ) : (
        <SettingsBody
          draft={draft}
          setDraft={setDraft}
          errors={errors}
          readOnly={!canWrite}
          inventoryDirty={Boolean(saved && JSON.stringify(saved.inventory) !== JSON.stringify(draft.inventory))}
          footer={
            canWrite && changeCount > 0 ? (
              <SaveBar
                count={changeCount}
                invalid={invalid}
                saving={saving}
                onDiscard={() => {
                  keepDraft.current = null;
                  if (saved) setDraft(saved);
                }}
                onSave={() => void save()}
              />
            ) : null
          }
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ body --- */

function SettingsBody({
  draft,
  setDraft,
  errors,
  readOnly,
  inventoryDirty,
  footer,
}: {
  draft: Draft;
  setDraft: (update: (current: Draft | null) => Draft | null) => void;
  errors: Errors;
  readOnly: boolean;
  inventoryDirty: boolean;
  footer: ReactNode;
}) {
  const { t } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<SectionId>("general");
  const sections = SECTIONS.filter(
    (section) => (section.id !== "purchasing" || draft.purchasing !== null) && (section.id !== "shopFloor" || draft.shopFloor !== null),
  );

  // The index follows the section at the top of the scroll area.
  useEffect(() => {
    const root = scroller.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        const first = visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (first) setActive(first.target.id.replace("settings-", "") as SectionId);
      },
      { root, rootMargin: "0px 0px -70% 0px" },
    );
    for (const section of sections) {
      const element = root.querySelector(`#settings-${section.id}`);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, []);

  const go = (id: SectionId) => {
    scroller.current?.querySelector(`#settings-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const patch = <K extends keyof Draft>(key: K, value: Partial<Draft[K]>) =>
    setDraft((current) => (current ? { ...current, [key]: { ...current[key], ...value } } : current));

  const sectionLabel: Record<SectionId, string> = {
    general: t("settings.section.general", "General"),
    products: t("settings.section.products", "Products"),
    inventory: t("settings.section.inventory", "Inventory"),
    production: t("settings.section.production", "Production"),
    shopFloor: t("settings.section.shop-floor", "Shop floor"),
    purchasing: t("settings.section.purchasing", "Purchasing"),
    pricing: t("settings.section.pricing", "Pricing"),
  };

  const g = draft.general;
  const pr = draft.product;
  const inv = draft.inventory;
  const p = draft.production;
  const pc = draft.pricing;
  const pu = draft.purchasing;
  const sf = draft.shopFloor;

  const sharePercent = Math.round(g.velocity_four_week_share * 100);

  return (
    <div ref={scroller} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex w-full max-w-6xl gap-8 px-4 py-5 lg:px-6">
        <nav aria-label={t("settings.title", "Settings")} className="sticky top-5 hidden h-fit w-44 shrink-0 lg:block">
          <ul className="flex flex-col gap-0.5">
            {sections.map(({ id, icon: Icon }) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => go(id)}
                  aria-current={active === id ? "true" : undefined}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors",
                    active === id ? "bg-accent-soft font-medium text-accent-strong" : "text-ink-muted hover:bg-surface-sunk hover:text-ink",
                  )}
                >
                  <Icon className="size-4 shrink-0" aria-hidden="true" />
                  {sectionLabel[id]}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col gap-10 pb-6">
          {/* ── General ─────────────────────────────────────────────────── */}
          <Section
            id="general"
            title={sectionLabel.general}
            description={t("settings.general.description", "Definitions every screen shares. Change one and the next KPI run recomputes every product with it.")}
          >
            <Group
              title={t("settings.velocity.title", "Sales velocity")}
              description={t(
                "settings.velocity.description",
                "Weekly units a product sells, the figure the pipeline plans on and the products page sorts by. Units sold inside bundles count for the single item.",
              )}
            >
              <Row
                label={t("settings.velocity.share", "Weight of the last 4 weeks")}
                hint={t("settings.velocity.share.hint", "The rest goes to the 12-week average. More weight reacts faster; less weight is steadier.")}
                error={errors["general.velocity_four_week_share"]}
              >
                <NumberInput
                  value={sharePercent}
                  unit="%"
                  step={5}
                  readOnly={readOnly}
                  invalid={Boolean(errors["general.velocity_four_week_share"])}
                  onChange={(value) => patch("general", { velocity_four_week_share: value === null ? Number.NaN : value / 100 })}
                />
              </Row>
              <Row
                label={t("settings.velocity.blend-after", "Blend after")}
                hint={t("settings.velocity.blend-after.hint", "Until a product has sold this long, its velocity is the 4-week average alone: its 12-week average would still be mostly empty weeks.")}
                error={errors["general.velocity_blend_after_weeks"]}
              >
                <NumberInput
                  value={g.velocity_blend_after_weeks}
                  unit={t("unit.weeks", "weeks")}
                  readOnly={readOnly}
                  invalid={Boolean(errors["general.velocity_blend_after_weeks"])}
                  onChange={(value) => patch("general", { velocity_blend_after_weeks: value ?? Number.NaN })}
                />
              </Row>
              <Formula>
                {t("settings.velocity.formula", "velocity = {{four}} % × 4-week average + {{twelve}} % × 12-week average, once on sale {{weeks}} weeks", {
                  four: Number.isFinite(sharePercent) ? sharePercent : "",
                  twelve: Number.isFinite(sharePercent) ? 100 - sharePercent : "",
                  weeks: Number.isFinite(g.velocity_blend_after_weeks) ? g.velocity_blend_after_weeks : "",
                })}
              </Formula>
            </Group>

            <Group
              title={t("settings.ranking.title", "Sales rank")}
              description={t(
                "settings.ranking.description",
                "Singles are ranked among singles and bundles among bundles, available and pending launch only. The rank decides a product's band in production below.",
              )}
            >
              <Row
                label={t("settings.ranking.basis", "Rank by")}
                hint={t("settings.ranking.basis.hint", "Ties go to the product with more units over the last 12 weeks.")}
              >
                <Select
                  value={g.sales_ranking_basis}
                  disabled={readOnly}
                  onChange={(event) => patch("general", { sales_ranking_basis: event.target.value as SalesRankingBasis })}
                  options={SALES_RANKING_BASES.map((basis) => ({ value: basis, label: basisLabel(basis, t) }))}
                  className="w-full sm:w-64"
                />
              </Row>
            </Group>

            <Group title={t("settings.new-products.title", "New products")}>
              <Row
                label={t("settings.new-products.days", "A product is new for")}
                hint={t("settings.new-products.days.hint", "Counted from its first sale. New products get their own safety stock in production and are never flagged for discontinuation.")}
                error={errors["general.new_product_days"]}
              >
                <NumberInput
                  value={g.new_product_days}
                  unit={t("unit.days", "days")}
                  readOnly={readOnly}
                  invalid={Boolean(errors["general.new_product_days"])}
                  onChange={(value) => patch("general", { new_product_days: value ?? Number.NaN })}
                />
              </Row>
            </Group>
          </Section>

          {/* ── Products ────────────────────────────────────────────────── */}
          <Section
            id="products"
            title={sectionLabel.products}
            description={t("settings.products.description", "The weekly job that retires slow movers, and how product images are ordered.")}
          >
            <Group
              title={t("settings.discontinuation.title", "Automatic discontinuation")}
              description={t(
                "settings.discontinuation.description",
                "A product is flagged when its 4-, 12- and 24-week averages are all under the threshold and it is no longer new. It is discontinued once the delay runs out, unless someone puts it on hold.",
              )}
            >
              <Row
                label={t("settings.discontinuation.enabled", "Weekly job")}
                hint={
                  pr.is_automatic_discontinuation_enabled
                    ? t("apd.enabled.hint.on", "Runs Mondays at 14:00; flags, counts down, discontinues, emails.")
                    : t("apd.enabled.hint.off", "Paused. Countdown dates stay as they are; the manual send still works.")
                }
              >
                <Switch
                  checked={pr.is_automatic_discontinuation_enabled}
                  disabled={readOnly}
                  onChange={(on) => patch("product", { is_automatic_discontinuation_enabled: on })}
                  label={pr.is_automatic_discontinuation_enabled ? t("common.on", "On") : t("common.off", "Off")}
                />
              </Row>
              <Row
                label={t("settings.discontinuation.single", "Single item threshold")}
                error={errors["product.discontinuation_single_item_threshold"]}
              >
                <NumberInput
                  value={pr.discontinuation_single_item_threshold}
                  unit={t("unit.per-week", "/ week")}
                  step={0.25}
                  readOnly={readOnly}
                  invalid={Boolean(errors["product.discontinuation_single_item_threshold"])}
                  onChange={(value) => patch("product", { discontinuation_single_item_threshold: value ?? Number.NaN })}
                />
              </Row>
              <Row
                label={t("settings.discontinuation.bundle", "Bundle threshold")}
                error={errors["product.discontinuation_bundle_threshold"]}
              >
                <NumberInput
                  value={pr.discontinuation_bundle_threshold}
                  unit={t("unit.per-week", "/ week")}
                  step={0.05}
                  readOnly={readOnly}
                  invalid={Boolean(errors["product.discontinuation_bundle_threshold"])}
                  onChange={(value) => patch("product", { discontinuation_bundle_threshold: value ?? Number.NaN })}
                />
              </Row>
              <Row
                label={t("settings.discontinuation.delay", "Delay before discontinuing")}
                hint={t("settings.discontinuation.delay.hint", "Between the flag and the status change.")}
                error={errors["product.discontinuation_delay_days"]}
              >
                <NumberInput
                  value={pr.discontinuation_delay_days}
                  unit={t("unit.days", "days")}
                  readOnly={readOnly}
                  invalid={Boolean(errors["product.discontinuation_delay_days"])}
                  onChange={(value) => patch("product", { discontinuation_delay_days: value ?? Number.NaN })}
                />
              </Row>
            </Group>

            <Group
              title={t("settings.image-types.title", "Image types")}
              description={t(
                "settings.image-types.description",
                "The order a product's images are grouped in on its Images tab. Drag to reorder; saved at once.",
              )}
            >
              <ImageTypeOrderGrid />
            </Group>

            <Group
              title={t("settings.shopify-images.single.title", "Shopify images: single items")}
              description={t(
                "settings.shopify-images.description",
                "The images each Shopify variant shows, in this order. Each row takes up to its quantity of images of its type (and orientation, when set) not taken by an earlier row, in the product's own order. Drag to reorder; saved at once. Applied on the next Shopify sync.",
              )}
            >
              <ShopifySingleItemImageRuleGrid />
            </Group>

            <Group title={t("settings.shopify-images.bundle.title", "Shopify images: bundles")}>
              <ShopifyBundleImageRuleGrid />
            </Group>
          </Section>

          {/* ── Inventory ───────────────────────────────────────────────── */}
          <Section
            id="inventory"
            title={sectionLabel.inventory}
            description={t("settings.inventory.description", "What each sales channel is told about stock.")}
          >
            <InventoryRulesGroup
              settings={inv}
              errors={errors}
              readOnly={readOnly}
              dirty={inventoryDirty}
              onChange={(value) => patch("inventory", value)}
            />
          </Section>

          {/* ── Production ──────────────────────────────────────────────── */}
          <Section
            id="production"
            title={sectionLabel.production}
            description={t("settings.production.description", "What the pipeline reads before it plans, and what the transfer planner keeps where.")}
          >
            <Group
              title={t("settings.bands.title", "Rank bands")}
              description={t(
                "settings.bands.description",
                "In weeks of a product's own velocity: a run starts when cover falls to the reorder point and is sized to land at the target. Safety stock is a floor the plan never goes under. A product takes the first band its rank fits in; the open band takes everyone else.",
              )}
            >
              <RankBandTable
                bands={draft.bands}
                errors={errors}
                readOnly={readOnly}
                onChange={(next) => setDraft((current) => (current ? { ...current, bands: next } : current))}
              />
            </Group>

            <Group title={t("settings.lots.title", "Lots and priority")}>
              <Row label={t("settings.lots.min", "Minimum lot size")} error={errors["production.min_lot_size"]}>
                <NumberInput value={p.min_lot_size} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.min_lot_size"])} onChange={(value) => patch("production", { min_lot_size: value ?? Number.NaN })} />
              </Row>
              <Row label={t("settings.lots.max", "Maximum lot size")} error={errors["production.max_lot_size"]}>
                <NumberInput value={p.max_lot_size} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.max_lot_size"])} onChange={(value) => patch("production", { max_lot_size: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.lots.weight-urgency", "Urgency weight")}
                hint={t("settings.lots.weights.hint", "Priority = urgency weight × how soon it runs out + performance weight × how well it sells.")}
                error={errors["production.weight_urgency"]}
              >
                <NumberInput value={p.weight_urgency} step={0.05} readOnly={readOnly} invalid={Boolean(errors["production.weight_urgency"])} onChange={(value) => patch("production", { weight_urgency: value ?? Number.NaN })} />
              </Row>
              <Row label={t("settings.lots.weight-performance", "Performance weight")} error={errors["production.weight_performance"]}>
                <NumberInput value={p.weight_performance} step={0.05} readOnly={readOnly} invalid={Boolean(errors["production.weight_performance"])} onChange={(value) => patch("production", { weight_performance: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.lots.paint", "Paint lead time")}
                hint={t("settings.lots.paint.hint", "Extra weeks of cover planned for products that go through paint.")}
                error={errors["production.paint_deficit_adjustment_in_weeks"]}
              >
                <NumberInput value={p.paint_deficit_adjustment_in_weeks} unit={t("unit.weeks", "weeks")} readOnly={readOnly} invalid={Boolean(errors["production.paint_deficit_adjustment_in_weeks"])} onChange={(value) => patch("production", { paint_deficit_adjustment_in_weeks: value ?? Number.NaN })} />
              </Row>
            </Group>

            <Group
              title={t("settings.production-new.title", "New products in production")}
              description={t(
                "settings.production-new.description",
                "A new product gets its first batch, then plans like any other product with the floor below. At or below the floor, counting what is already in production, it gets a run even before it has a velocity.",
              )}
            >
              <Row
                label={t("settings.lots.first-batch", "First batch lot size")}
                hint={t("settings.lots.first-batch.hint", "A product's first run, before it has sales to size one on. Shown as JUST LAUNCHED on the pipeline.")}
                error={errors["production.first_batch_lot_size"]}
              >
                <NumberInput value={p.first_batch_lot_size} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.first_batch_lot_size"])} onChange={(value) => patch("production", { first_batch_lot_size: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.production-new.safety", "Safety stock")}
                hint={t("settings.production-new.safety.hint", "Its floor while it is new. A new product in a band with more safety stock takes the larger.")}
                error={errors["production.new_product_safety_stock"]}
              >
                <NumberInput value={p.new_product_safety_stock} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.new_product_safety_stock"])} onChange={(value) => patch("production", { new_product_safety_stock: value ?? Number.NaN })} />
              </Row>
            </Group>

            <Group
              title={t("settings.transfers.title", "Transfers between warehouses")}
              description={t("settings.transfers.description", "The defaults of the transfer planner. Cover and reserve can still be changed on the transfer screen for one run.")}
            >
              <Row
                label={t("settings.transfers.cover", "Fill destinations to")}
                error={errors["production.transfer_cover_weeks"]}
              >
                <NumberInput value={p.transfer_cover_weeks} unit={t("unit.weeks-cover", "weeks of cover")} readOnly={readOnly} invalid={Boolean(errors["production.transfer_cover_weeks"])} onChange={(value) => patch("production", { transfer_cover_weeks: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.transfers.reserve", "Sources keep")}
                hint={t("settings.transfers.reserve.hint", "Weeks of their own demand, before anything leaves.")}
                error={errors["production.transfer_reserve_weeks"]}
              >
                <NumberInput value={p.transfer_reserve_weeks} unit={t("unit.weeks", "weeks")} readOnly={readOnly} invalid={Boolean(errors["production.transfer_reserve_weeks"])} onChange={(value) => patch("production", { transfer_reserve_weeks: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.transfers.minimum-reserve", "Smallest reserve")}
                hint={t("settings.transfers.minimum-reserve.hint", "What a source keeps of anything it sells at all, however slowly.")}
                error={errors["production.transfer_minimum_reserve_units"]}
              >
                <NumberInput value={p.transfer_minimum_reserve_units} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.transfer_minimum_reserve_units"])} onChange={(value) => patch("production", { transfer_minimum_reserve_units: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.transfers.rounding", "Round transfers to")}
                hint={t("settings.transfers.rounding.hint", "Quantities move in multiples of this.")}
                error={errors["production.transfer_rounding_units"]}
              >
                <NumberInput value={p.transfer_rounding_units} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.transfer_rounding_units"])} onChange={(value) => patch("production", { transfer_rounding_units: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.transfers.seed", "Seed quantity")}
                hint={t("settings.transfers.seed.hint", "Sent to a warehouse that has never sold a product, so it can start to.")}
                error={errors["production.transfer_seed_units"]}
              >
                <NumberInput value={p.transfer_seed_units} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["production.transfer_seed_units"])} onChange={(value) => patch("production", { transfer_seed_units: value ?? Number.NaN })} />
              </Row>
            </Group>

            <Group
              title={t("settings.kanban.title", "Kanban runs")}
              description={t(
                "settings.kanban.description",
                "Kanban parts are planned like bought materials, on their stocking point: a run covers its usage while it is made and until the next one.",
              )}
            >
              <Row label={t("settings.kanban.lead-time", "A run takes")} error={errors["production.kanban_lead_time_days"]}>
                <NumberInput value={p.kanban_lead_time_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["production.kanban_lead_time_days"])} onChange={(value) => patch("production", { kanban_lead_time_days: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.kanban.cycle", "Days between runs")}
                hint={t("settings.kanban.cycle.hint", "How often the same kanban part is run: each run covers this much usage.")}
                error={errors["production.kanban_cycle_days"]}
              >
                <NumberInput value={p.kanban_cycle_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["production.kanban_cycle_days"])} onChange={(value) => patch("production", { kanban_cycle_days: value ?? Number.NaN })} />
              </Row>
            </Group>

            <Group
              title={t("settings.orders.title", "Production and assembly orders")}
              description={t(
                "settings.orders.description",
                "A pipeline lot becomes a production order only when its Laval materials are in stock. Its sets are shared between the product's assembly versions, a split that can change for a while after the first ones arrive.",
              )}
            >
              <Row
                label={t("settings.orders.override", "Shortage override")}
                hint={t("settings.orders.override.hint", "A ROOT user may still make the lots that are short, with a reason, until the go-live count makes stock trustworthy. Switch it off at cutover.")}
              >
                <Switch
                  checked={p.is_shortage_override_allowed}
                  disabled={readOnly}
                  onChange={(checked) => patch("production", { is_shortage_override_allowed: checked })}
                  label={p.is_shortage_override_allowed ? t("common.on", "On") : t("common.off", "Off")}
                />
              </Row>
              <Row
                label={t("settings.orders.version-routing", "Ship by assembly version")}
                hint={t(
                  "settings.orders.version-routing.hint",
                  "Off until cutover. On, the Deringer export, parcel tracking, retail exports and feeds take each product's boxes and ship SKU from the version its client's rules give (Version rules), instead of its packages and the us_version metafield.",
                )}
              >
                <Switch
                  checked={p.is_version_routing}
                  disabled={readOnly}
                  onChange={(checked) => patch("production", { is_version_routing: checked })}
                  label={p.is_version_routing ? t("common.on", "On") : t("common.off", "Off")}
                />
              </Row>
              <Row
                label={t("settings.orders.split-window", "Split open for")}
                hint={t("settings.orders.split-window.hint", "After a lot's first sets arrive at Terrebonne; never once a version's assembly has started.")}
                error={errors["production.assembly_split_window_hours"]}
              >
                <NumberInput value={p.assembly_split_window_hours} unit={t("unit.hours", "hours")} readOnly={readOnly} invalid={Boolean(errors["production.assembly_split_window_hours"])} onChange={(value) => patch("production", { assembly_split_window_hours: value ?? Number.NaN })} />
              </Row>
            </Group>

            <Group
              title={t("settings.lifecycle.title", "Part lifecycle")}
              description={t(
                "settings.lifecycle.description",
                "Material parts follow the products that use them: inactive once no product that is not discontinued uses them, active again when one does. Finished parts follow their product. Try it first as a dry run from the Actions page.",
              )}
            >
              <Row label={t("settings.lifecycle.enabled", "Update part status every night")}>
                <Switch
                  checked={p.is_part_lifecycle_automatic}
                  disabled={readOnly}
                  onChange={(checked) => patch("production", { is_part_lifecycle_automatic: checked })}
                  label={p.is_part_lifecycle_automatic ? t("common.on", "On") : t("common.off", "Off")}
                />
              </Row>
            </Group>
          </Section>

          {/* ── Shop floor ──────────────────────────────────────────────── */}
          {sf && (
            <Section
              id="shopFloor"
              title={sectionLabel.shopFloor}
              description={t(
                "settings.shop-floor.description",
                "How the floor boards and kiosks behave, what the lot documents print, and the Terrebonne packing line.",
              )}
            >
              <Group
                title={t("settings.shop-floor.boards.title", "Boards and kiosks")}
                description={t("settings.shop-floor.boards.description", "The shop-floor board, the Terrebonne board and every station kiosk ask for news this often.")}
              >
                <Row
                  label={t("settings.shop-floor.refresh", "Refresh every")}
                  hint={t("settings.shop-floor.refresh.hint", "At least 10 seconds. A minute is plenty for a TV board.")}
                  error={errors["shopFloor.board_refresh_seconds"]}
                >
                  <NumberInput value={sf.board_refresh_seconds} unit={t("unit.seconds", "seconds")} step={10} readOnly={readOnly} invalid={Boolean(errors["shopFloor.board_refresh_seconds"])} onChange={(value) => patch("shopFloor", { board_refresh_seconds: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.operator-idle", "Forget a badge after")}
                  hint={t("settings.shop-floor.operator-idle.hint", "A kiosk goes back to anonymous after this long without a report.")}
                  error={errors["shopFloor.operator_idle_minutes"]}
                >
                  <NumberInput value={sf.operator_idle_minutes} unit={t("unit.minutes", "minutes")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.operator_idle_minutes"])} onChange={(value) => patch("shopFloor", { operator_idle_minutes: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.stalled", "Stalled after")}
                  hint={t("settings.shop-floor.stalled.hint", "A started step with nothing reported for this long is flagged on the boards.")}
                  error={errors["shopFloor.stalled_after_hours"]}
                >
                  <NumberInput value={sf.stalled_after_hours} unit={t("unit.hours", "hours")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.stalled_after_hours"])} onChange={(value) => patch("shopFloor", { stalled_after_hours: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.backflush", "Consume materials every")}
                  hint={t("settings.shop-floor.backflush.hint", "How often the backflush sweep takes from stock what the floor's progress has used.")}
                  error={errors["shopFloor.backflush_interval_minutes"]}
                >
                  <NumberInput value={sf.backflush_interval_minutes} unit={t("unit.minutes", "minutes")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.backflush_interval_minutes"])} onChange={(value) => patch("shopFloor", { backflush_interval_minutes: value ?? Number.NaN })} />
                </Row>
              </Group>

              <Group
                title={t("settings.shop-floor.documents.title", "Lot documents")}
                description={t("settings.shop-floor.documents.description", "What the lot package prints.")}
              >
                <Row
                  label={t("settings.shop-floor.label-extra", "Extra service labels")}
                  hint={t("settings.shop-floor.label-extra.hint", "Printed beyond the lot quantity, for the ones that tear.")}
                  error={errors["shopFloor.service_label_extra"]}
                >
                  <NumberInput value={sf.service_label_extra} unit={t("unit.labels", "labels")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.service_label_extra"])} onChange={(value) => patch("shopFloor", { service_label_extra: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.sheet-copies", "Production sheets")}
                  error={errors["shopFloor.production_sheet_copies"]}
                >
                  <NumberInput value={sf.production_sheet_copies} unit={t("unit.copies", "copies")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.production_sheet_copies"])} onChange={(value) => patch("shopFloor", { production_sheet_copies: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.sheet-painted", "Production sheets with painted parts")}
                  hint={t("settings.shop-floor.sheet-painted.hint", "A lot with painted (MDF) parts follows a slightly different flow and gets more copies.")}
                  error={errors["shopFloor.production_sheet_painted_copies"]}
                >
                  <NumberInput value={sf.production_sheet_painted_copies} unit={t("unit.copies", "copies")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.production_sheet_painted_copies"])} onChange={(value) => patch("shopFloor", { production_sheet_painted_copies: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.long-pallet", "Long pallet from")}
                  hint={t("settings.shop-floor.long-pallet.hint", "A part longer or wider than this goes on a long pallet in the cut list.")}
                  error={errors["shopFloor.long_pallet_threshold_mm"]}
                >
                  <NumberInput value={sf.long_pallet_threshold_mm} unit="mm" step={10} readOnly={readOnly} invalid={Boolean(errors["shopFloor.long_pallet_threshold_mm"])} onChange={(value) => patch("shopFloor", { long_pallet_threshold_mm: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.shop-floor.cutlist-quantity", "SKU cut list quantity")}
                  hint={t("settings.shop-floor.cutlist-quantity.hint", "A cut list asked for a SKU with no quantity is made for this many.")}
                  error={errors["shopFloor.cutlist_default_quantity"]}
                >
                  <NumberInput value={sf.cutlist_default_quantity} unit={t("unit.units", "units")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.cutlist_default_quantity"])} onChange={(value) => patch("shopFloor", { cutlist_default_quantity: value ?? Number.NaN })} />
                </Row>
              </Group>

              <Group
                title={t("settings.shop-floor.packing.title", "Terrebonne packing line")}
                description={t("settings.shop-floor.packing.description", "The packing time estimate: box length plus spacing, at the line speed.")}
              >
                <Row label={t("settings.shop-floor.line-speed", "Line speed")} error={errors["shopFloor.packing_line_speed"]}>
                  <NumberInput value={sf.packing_line_speed} unit={t("unit.inches-per-minute", "in/min")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.packing_line_speed"])} onChange={(value) => patch("shopFloor", { packing_line_speed: value ?? Number.NaN })} />
                </Row>
                <Row label={t("settings.shop-floor.box-spacing", "Space between boxes")} error={errors["shopFloor.packing_box_spacing"]}>
                  <NumberInput value={sf.packing_box_spacing} unit={t("unit.inches", "in")} readOnly={readOnly} invalid={Boolean(errors["shopFloor.packing_box_spacing"])} onChange={(value) => patch("shopFloor", { packing_box_spacing: value ?? Number.NaN })} />
                </Row>
              </Group>
            </Section>
          )}

          {/* ── Purchasing ──────────────────────────────────────────────── */}
          {pu && (
            <Section
              id="purchasing"
              title={sectionLabel.purchasing}
              description={t(
                "settings.purchasing.description",
                "What the replenishment run plans with when a supplier or a part says nothing, who approves big orders, and how far the cash projection looks. Safety days are set per part group, on the Part groups page.",
              )}
            >
              <Group
                title={t("settings.purchasing.approval.title", "Approval")}
                description={t("settings.purchasing.approval.description", "An order at or above this total waits for an approver before its supplier sees it. Totals are compared in CAD.")}
              >
                <Row
                  label={t("settings.purchasing.approval.threshold", "Approval threshold")}
                  hint={t("settings.purchasing.approval.threshold.hint", "Approvers are the accounts granted \"Approve purchase orders\". 0 sends every order to approval.")}
                  error={errors["purchasing.approval_threshold_amount"]}
                >
                  <NumberInput value={pu.approval_threshold_amount} unit="CAD" step={500} readOnly={readOnly} invalid={Boolean(errors["purchasing.approval_threshold_amount"])} onChange={(value) => patch("purchasing", { approval_threshold_amount: value ?? Number.NaN })} />
                </Row>
              </Group>

              <Group
                title={t("settings.purchasing.planning.title", "Planning defaults")}
                description={t("settings.purchasing.planning.description", "Used for any supplier or part that has no figure of its own.")}
              >
                <Row
                  label={t("settings.purchasing.usage-window", "Usage window")}
                  hint={t("settings.purchasing.usage-window.hint", "Average daily usage is what was used over this many days, divided by them. 91 days is a quarter.")}
                  error={errors["purchasing.usage_window_days"]}
                >
                  <NumberInput value={pu.usage_window_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.usage_window_days"])} onChange={(value) => patch("purchasing", { usage_window_days: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.lead-time", "Lead time")}
                  hint={t("settings.purchasing.lead-time.hint", "Days from release to arrival, for a supplier with none typed or measured.")}
                  error={errors["purchasing.default_lead_time_days"]}
                >
                  <NumberInput value={pu.default_lead_time_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.default_lead_time_days"])} onChange={(value) => patch("purchasing", { default_lead_time_days: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.order-cycle", "Order cycle")}
                  hint={t("settings.purchasing.order-cycle.hint", "Days between two orders to the same supplier: how much is bought at a time, the green zone.")}
                  error={errors["purchasing.default_order_cycle_days"]}
                >
                  <NumberInput value={pu.default_order_cycle_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.default_order_cycle_days"])} onChange={(value) => patch("purchasing", { default_order_cycle_days: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.payment-terms", "Payment terms")}
                  hint={t("settings.purchasing.payment-terms-kind.hint", "Terms a new supplier starts with. A deposit needs a percentage, so it is set on the supplier.")}
                >
                  <Select
                    value={pu.default_payment_terms}
                    disabled={readOnly}
                    onChange={(event) => patch("purchasing", { default_payment_terms: event.target.value as PurchasingSettingsDTO["default_payment_terms"] })}
                    options={SUPPLIER_PAYMENT_TERMS.filter((terms) => terms !== "DEPOSIT").map((terms) => ({ value: terms, label: paymentTermsName(terms, t) }))}
                    className="w-full sm:w-64"
                  />
                </Row>
                <Row
                  label={t("settings.purchasing.payment-terms-days", "Net days")}
                  hint={t("settings.purchasing.payment-terms.hint", "Days after receipt a net supplier with none typed is paid, for the cash projection.")}
                  error={errors["purchasing.default_payment_terms_days"]}
                >
                  <NumberInput value={pu.default_payment_terms_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.default_payment_terms_days"])} onChange={(value) => patch("purchasing", { default_payment_terms_days: value ?? Number.NaN })} />
                </Row>
              </Group>

              <Group
                title={t("settings.purchasing.measured.title", "Measured lead times")}
                description={t(
                  "settings.purchasing.measured.description",
                  "A supplier in measured mode is planned with the median of its own receipts (from the day an order was released to its first receipt) once it has enough of them.",
                )}
              >
                <Row
                  label={t("settings.purchasing.measured.receipts", "Receipts needed")}
                  hint={t("settings.purchasing.measured.receipts.hint", "Below this, the typed lead time is used instead.")}
                  error={errors["purchasing.measured_lead_time_minimum_receipts"]}
                >
                  <NumberInput value={pu.measured_lead_time_minimum_receipts} unit={t("unit.receipts", "receipts")} readOnly={readOnly} invalid={Boolean(errors["purchasing.measured_lead_time_minimum_receipts"])} onChange={(value) => patch("purchasing", { measured_lead_time_minimum_receipts: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.measured.window", "Look back")}
                  hint={t("settings.purchasing.measured.window.hint", "Only receipts this recent are measured.")}
                  error={errors["purchasing.measured_lead_time_window_days"]}
                >
                  <NumberInput value={pu.measured_lead_time_window_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.measured_lead_time_window_days"])} onChange={(value) => patch("purchasing", { measured_lead_time_window_days: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.measured.drift", "Flag drift beyond")}
                  hint={t("settings.purchasing.measured.drift.hint", "A supplier whose measured lead time is this much off its typed one is flagged on the Suppliers page and in the review.")}
                  error={errors["purchasing.lead_time_drift_percent"]}
                >
                  <NumberInput value={pu.lead_time_drift_percent} unit="%" step={5} readOnly={readOnly} invalid={Boolean(errors["purchasing.lead_time_drift_percent"])} onChange={(value) => patch("purchasing", { lead_time_drift_percent: value ?? Number.NaN })} />
                </Row>
              </Group>

              <Group
                title={t("settings.purchasing.follow-up.title", "Cash and reviews")}
                description={t("settings.purchasing.follow-up.description", "How far ahead the cash projection looks, and what the chase lists and the monthly review count.")}
              >
                <Row
                  label={t("settings.purchasing.cash-weeks", "Cash projection")}
                  hint={t("settings.purchasing.cash-weeks.hint", "Weeks shown, from this week. Between 4 and 52.")}
                  error={errors["purchasing.cash_projection_weeks"]}
                >
                  <NumberInput value={pu.cash_projection_weeks} unit={t("unit.weeks", "weeks")} readOnly={readOnly} invalid={Boolean(errors["purchasing.cash_projection_weeks"])} onChange={(value) => patch("purchasing", { cash_projection_weeks: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.hold-alert", "Flag holds after")}
                  hint={t("settings.purchasing.hold-alert.hint", "An issued order still on hold this long is flagged: a hold means \"wait for the truck\", not \"forget\".")}
                  error={errors["purchasing.hold_alert_days"]}
                >
                  <NumberInput value={pu.hold_alert_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.hold_alert_days"])} onChange={(value) => patch("purchasing", { hold_alert_days: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.stale", "Give up on lines after")}
                  hint={t("settings.purchasing.stale.hint", "An issued line expected longer ago than this is taken as dead: not counted as on order, left out of the cash projection, and listed as stale for closing. At least 30.")}
                  error={errors["purchasing.stale_order_days"]}
                >
                  <NumberInput value={pu.stale_order_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.stale_order_days"])} onChange={(value) => patch("purchasing", { stale_order_days: value ?? Number.NaN })} />
                </Row>
                <Row
                  label={t("settings.purchasing.review-window", "Safety-days review")}
                  hint={t("settings.purchasing.review-window.hint", "Days the monthly review looks back over when it suggests more or fewer safety days.")}
                  error={errors["purchasing.review_window_days"]}
                >
                  <NumberInput value={pu.review_window_days} unit={t("unit.days", "days")} readOnly={readOnly} invalid={Boolean(errors["purchasing.review_window_days"])} onChange={(value) => patch("purchasing", { review_window_days: value ?? Number.NaN })} />
                </Row>
              </Group>

              <Group
                title={t("settings.purchasing.automatic.title", "Automatic drafts")}
                description={t(
                  "settings.purchasing.automatic.description",
                  "When on, the nightly run turns what is left to order into draft orders, one per supplier and warehouse, and replaces its own untouched drafts each night. Nothing reaches a supplier until a person issues it.",
                )}
              >
                <Row label={t("settings.purchasing.automatic.enabled", "Prepare drafts every night")}>
                  <Switch
                    checked={pu.is_automatic_draft_enabled}
                    disabled={readOnly}
                    onChange={(checked) => patch("purchasing", { is_automatic_draft_enabled: checked })}
                    label={pu.is_automatic_draft_enabled ? t("common.on", "On") : t("common.off", "Off")}
                  />
                </Row>
              </Group>
              <Group
                title={t("settings.purchasing.pipeline.title", "The planning")}
                description={t(
                  "settings.purchasing.pipeline.description",
                  "Purchasing runs on usage and on what confirmed production orders reserve. When on, the planning's lots not yet made into orders count as reserved too; off, they are only shown.",
                )}
              >
                <Row label={t("settings.purchasing.pipeline.enabled", "Count the planning as reserved")}>
                  <Switch
                    checked={pu.is_planning_in_position ?? false}
                    disabled={readOnly}
                    onChange={(checked) => patch("purchasing", { is_planning_in_position: checked })}
                    label={pu.is_planning_in_position ? t("common.on", "On") : t("common.off", "Off")}
                  />
                </Row>
              </Group>
            </Section>
          )}

          {/* ── Pricing ─────────────────────────────────────────────────── */}
          <Section
            id="pricing"
            title={sectionLabel.pricing}
            description={t("settings.pricing.description", "What the pricing engine starts from.")}
          >
            <Group title={t("settings.margins.title", "Margins")}>
              <Row
                label={t("settings.margins.gross", "Target gross margin")}
                hint={t("settings.margins.gross.hint", "Applied to every product without a pricing rule of its own.")}
                error={errors["pricing.gross_margin_percent"]}
              >
                <NumberInput value={pc.gross_margin_percent} unit="%" readOnly={readOnly} invalid={Boolean(errors["pricing.gross_margin_percent"])} onChange={(value) => patch("pricing", { gross_margin_percent: value ?? Number.NaN })} />
              </Row>
              <Row
                label={t("settings.margins.discount", "Lowest net margin after discount")}
                hint={t("settings.margins.discount.hint", "The discount report marks any discount that would take a product below this.")}
                error={errors["pricing.minimum_discount_net_margin_percent"]}
              >
                <NumberInput value={pc.minimum_discount_net_margin_percent} unit="%" readOnly={readOnly} invalid={Boolean(errors["pricing.minimum_discount_net_margin_percent"])} onChange={(value) => patch("pricing", { minimum_discount_net_margin_percent: value ?? Number.NaN })} />
              </Row>
            </Group>

            <Group
              title={t("settings.reference-period.title", "Client pricing reference period")}
              description={t("settings.reference-period.description", "The sales window client pricing is measured against.")}
            >
              <Row label={t("period.from", "From")} error={errors["pricing.reference_period"]}>
                <Input
                  type="date"
                  value={pc.reference_period_start_date ?? ""}
                  readOnly={readOnly}
                  invalid={Boolean(errors["pricing.reference_period"])}
                  onChange={(event) => patch("pricing", { reference_period_start_date: event.target.value })}
                  className="w-full sm:w-44"
                />
              </Row>
              <Row label={t("period.to", "To")}>
                <Input
                  type="date"
                  value={pc.reference_period_end_date ?? ""}
                  readOnly={readOnly}
                  invalid={Boolean(errors["pricing.reference_period"])}
                  onChange={(event) => patch("pricing", { reference_period_end_date: event.target.value })}
                  className="w-full sm:w-44"
                />
              </Row>
            </Group>

            <Group
              title={t("settings.rates.title", "Exchange rates")}
              description={t("settings.rates.description", "Applied when converting costs and sales between currencies.")}
            >
              {draft.rates.map((rate, index) => (
                <Row key={rate.id} label={rate.currency} error={errors[`rates.${index}`]}>
                  <NumberInput
                    value={rate.rate}
                    step={0.01}
                    readOnly={readOnly}
                    invalid={Boolean(errors[`rates.${index}`])}
                    onChange={(value) =>
                      setDraft((current) =>
                        current
                          ? { ...current, rates: current.rates.map((row, at) => (at === index ? { ...row, rate: value ?? Number.NaN } : row)) }
                          : current,
                      )
                    }
                  />
                </Row>
              ))}
            </Group>
          </Section>
        </div>
      </div>
      {footer}
    </div>
  );
}

/* ------------------------------------------------------------ rank bands --- */

function RankBandTable({
  bands,
  errors,
  readOnly,
  onChange,
}: {
  bands: RankBandDTO[];
  errors: Errors;
  readOnly: boolean;
  onChange: (next: RankBandDTO[]) => void;
}) {
  const { t } = useTranslation();
  const set = (index: number, value: Partial<RankBandDTO>) =>
    onChange(bands.map((band, at) => (at === index ? { ...band, ...value } : band)));

  // The ranks each band actually covers, from the bands above it: what the
  // pipeline will do, which a list of upper bounds leaves to mental arithmetic.
  const ordered = sortBands(bands);
  const coverage = new Map<number, string>();
  let from = 1;
  for (const band of ordered) {
    if (band.rank_to === null) continue;
    coverage.set(band.id, from > band.rank_to ? "" : from === band.rank_to ? `#${from}` : `#${from}–${band.rank_to}`);
    from = Math.max(from, band.rank_to + 1);
  }
  for (const band of ordered) {
    if (band.rank_to === null) coverage.set(band.id, t("settings.bands.rest", "#{{from}}+ and unranked", { from }));
  }

  const add = () => {
    const bounded = bands.filter((band) => band.rank_to !== null).map((band) => band.rank_to as number);
    const highest = bounded.length ? Math.max(...bounded) : 0;
    const open = bands.find((band) => band.rank_to === null);
    const template = open ?? bands[bands.length - 1];
    const created: RankBandDTO = {
      id: nextTemporaryId--,
      rank_to: highest + 50,
      reorder_weeks: template?.reorder_weeks ?? 3,
      target_weeks: template?.target_weeks ?? 6,
      safety_stock_quantity: 0,
    };
    onChange(sortBands([...bands, created]));
  };

  const headCell = "px-2 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-ink-faint";

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-line">
              <th className={headCell}>{t("settings.bands.rank-to", "Up to rank")}</th>
              <th className={headCell}>{t("settings.bands.covers", "Covers")}</th>
              <th className={headCell}>{t("settings.bands.reorder", "Reorder at")}</th>
              <th className={headCell}>{t("settings.bands.target", "Target")}</th>
              <th className={headCell}>{t("settings.bands.safety", "Safety stock")}</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {bands.map((band, index) => {
              const error = errors[`bands.${index}`];
              return (
                <tr key={band.id} className="border-b border-line-soft align-top last:border-b-0">
                  <td className="px-2 py-2">
                    <NumberInput
                      value={band.rank_to}
                      placeholder={t("settings.bands.open", "Open")}
                      readOnly={readOnly}
                      invalid={Boolean(error)}
                      onChange={(value) => set(index, { rank_to: value })}
                      aria-label={t("settings.bands.rank-to", "Up to rank")}
                      compact
                    />
                    {error && <p className="mt-1 max-w-56 text-[12px] text-risk">{error}</p>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 pt-4 text-[12px] tabular-nums text-ink-muted">{coverage.get(band.id)}</td>
                  <td className="px-2 py-2">
                    <NumberInput value={band.reorder_weeks} unit={t("unit.weeks-short", "wk")} readOnly={readOnly} onChange={(value) => set(index, { reorder_weeks: value ?? Number.NaN })} aria-label={t("settings.bands.reorder", "Reorder at")} compact />
                  </td>
                  <td className="px-2 py-2">
                    <NumberInput value={band.target_weeks} unit={t("unit.weeks-short", "wk")} readOnly={readOnly} onChange={(value) => set(index, { target_weeks: value ?? Number.NaN })} aria-label={t("settings.bands.target", "Target")} compact />
                  </td>
                  <td className="px-2 py-2">
                    <NumberInput value={band.safety_stock_quantity} unit={t("unit.units-short", "u")} readOnly={readOnly} onChange={(value) => set(index, { safety_stock_quantity: value ?? Number.NaN })} aria-label={t("settings.bands.safety", "Safety stock")} compact />
                  </td>
                  <td className="px-1 py-2">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => onChange(bands.filter((_, at) => at !== index))}
                        className="grid size-9 place-items-center rounded-md text-ink-faint transition-colors hover:bg-risk-soft hover:text-risk"
                        aria-label={t("button.delete", "Delete")}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {errors.bands && <p className="text-[12px] text-risk">{errors.bands}</p>}
      {!readOnly && (
        <div>
          <Button variant="ghost" size="sm" onClick={add}>
            <Plus className="size-4" aria-hidden="true" />
            {t("settings.bands.add", "Add band")}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ primitives --- */

function Section({ id, title, description, children }: { id: SectionId; title: string; description?: string; children: ReactNode }) {
  return (
    <section id={`settings-${id}`} className="flex scroll-mt-5 flex-col gap-4">
      <header>
        <h2 className="text-[17px] font-semibold tracking-tight text-ink">{title}</h2>
        {description && <p className="mt-1 max-w-prose text-[13px] text-ink-muted">{description}</p>}
      </header>
      {children}
    </section>
  );
}

function Group({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-line-soft bg-surface-sunk/40 px-5 py-3">
        <h3 className="text-[13px] font-semibold text-ink">{title}</h3>
        {description && <p className="mt-0.5 max-w-prose text-[12px] leading-relaxed text-ink-muted">{description}</p>}
      </div>
      <div className="divide-y divide-line-soft">{children}</div>
    </Card>
  );
}

function Row({
  label,
  hint,
  detail,
  error,
  children,
}: {
  label: string;
  hint?: string;
  /** Richer content under the hint, e.g. a status line. */
  detail?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-ink">{label}</div>
        {hint && <p className="mt-0.5 max-w-prose text-[12px] leading-relaxed text-ink-muted">{hint}</p>}
        {detail}
        {error && <p className="mt-0.5 text-[12px] text-risk">{error}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Formula({ children }: { children: ReactNode }) {
  return (
    <div className="bg-surface-sunk/60 px-5 py-2.5 font-mono text-[12px] text-ink-muted">{children}</div>
  );
}

function NumberInput({
  value,
  onChange,
  unit,
  step,
  readOnly,
  invalid,
  placeholder,
  compact,
  "aria-label": ariaLabel,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  unit?: string;
  step?: number;
  readOnly?: boolean;
  invalid?: boolean;
  placeholder?: string;
  compact?: boolean;
  "aria-label"?: string;
}) {
  return (
    <div className={cn("relative", compact ? "w-24" : "w-full sm:w-44")}>
      <Input
        type="number"
        inputMode="decimal"
        step={step ?? "any"}
        value={value === null || Number.isNaN(value) ? "" : String(value)}
        placeholder={placeholder}
        readOnly={readOnly}
        invalid={invalid}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.value === "" ? null : Number(event.target.value))}
        className="text-right tabular-nums"
        // Room for the unit, sized to it: a fixed pad either crowds "weeks of
        // cover" or pushes a short number away from its "%".
        style={unit ? { paddingRight: `${unit.length * 0.42 + 1.5}rem` } : undefined}
      />
      {unit && (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[12px] text-ink-faint">{unit}</span>
      )}
    </div>
  );
}

function SaveBar({
  count,
  invalid,
  saving,
  onDiscard,
  onSave,
}: {
  count: number;
  invalid: boolean;
  saving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="sticky bottom-0 z-10 border-t border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-end gap-3 px-4 py-3 lg:px-6">
        <span className={cn("mr-auto text-[13px]", invalid ? "text-risk" : "text-ink-muted")}>
          {invalid
            ? t("settings.invalid", "Some values need fixing before they can be saved.")
            : t("config.unsaved", "{{count}} unsaved change(s)", { count })}
        </span>
        <Button variant="ghost" onClick={onDiscard} disabled={saving}>
          {t("button.discard", "Discard")}
        </Button>
        <Button variant="primary" loading={saving} disabled={invalid} onClick={onSave}>
          {t("button.save", "Save")}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ validation, diff --- */

function basisLabel(basis: SalesRankingBasis, t: (key: string, fallback: string) => string): string {
  switch (basis) {
    case "VELOCITY":
      return t("settings.ranking.basis.velocity", "Velocity (4/12-week blend)");
    case "FOUR_WEEK_AVERAGE":
      return t("settings.ranking.basis.four", "4-week average");
    case "TWELVE_WEEK_AVERAGE":
      return t("settings.ranking.basis.twelve", "12-week average");
    case "TWELVE_WEEK_UNITS":
      return t("settings.ranking.basis.units", "Units, last 12 weeks");
  }
}

/** The same rules as the database's CHECK constraints, said before the save instead of as a 500 after it. */
/* -------------------------------------------------------- inventory rules --- */

/**
 * The daily inventory rule job: its switch, the top N per market, the last
 * run, and the way to preview and apply it now. The floor itself is per
 * channel (Sales channels page); locked rules are left alone.
 */
function InventoryRulesGroup({
  settings,
  errors,
  readOnly,
  dirty,
  onChange,
}: {
  settings: InventorySettingsDTO;
  errors: Errors;
  readOnly: boolean;
  /** The section has unsaved changes: a run would read the saved values, so it waits. */
  dirty: boolean;
  onChange: (value: Partial<InventorySettingsDTO>) => void;
}) {
  const { t } = useTranslation();
  const [jobOpen, setJobOpen] = useState(false);
  const lastRun = useLastInventoryRuleRun();
  const run = lastRun.data;

  return (
    <Group
      title={t("settings.inventory-rules.title", "Inventory rules")}
      description={t(
        "settings.inventory-rules.description",
        "Every night, after the KPI run, each unlocked rule is set: the top single items of a market, and its new products and products in production where those are switched on, floor on that market's channels at the channel's default floor; discontinued and not-yet-launched products are blocked; everything else, bundles included, is dispatchable. Components are always blocked and unlisted, and a product pending discontinuation never floors.",
      )}
    >
      <Row
        label={t("settings.inventory-rules.enabled", "Daily job")}
        hint={
          settings.is_automation_enabled
            ? t("settings.inventory-rules.enabled.on", "Runs at the end of every KPI recompute.")
            : t("settings.inventory-rules.enabled.off", "Off: rules change only by hand. Apply now still works.")
        }
      >
        <Switch
          checked={settings.is_automation_enabled}
          disabled={readOnly}
          onChange={(on) => onChange({ is_automation_enabled: on })}
          label={settings.is_automation_enabled ? t("common.on", "On") : t("common.off", "Off")}
        />
      </Row>
      <Row
        label={t("settings.inventory-rules.top-ca", "Floored in Canada")}
        hint={t("settings.inventory-rules.top.hint", "Single items ranked this high or better in the market's own sales. 0: none.")}
        error={errors["inventory.floor_top_ca"]}
      >
        <NumberInput
          value={settings.floor_top_ca}
          unit={t("settings.inventory-rules.top.unit", "top products")}
          readOnly={readOnly}
          invalid={Boolean(errors["inventory.floor_top_ca"])}
          onChange={(value) => onChange({ floor_top_ca: value ?? Number.NaN })}
        />
      </Row>
      <Row
        label={t("settings.inventory-rules.new-ca", "New products in Canada")}
        hint={t(
          "settings.inventory-rules.new.hint",
          "Also floor available single items first sold less than the new-product days ago (General settings), or not sold yet.",
        )}
      >
        <Switch
          checked={settings.is_floor_new_ca}
          disabled={readOnly}
          onChange={(on) => onChange({ is_floor_new_ca: on })}
          label={settings.is_floor_new_ca ? t("common.on", "On") : t("common.off", "Off")}
        />
      </Row>
      <Row
        label={t("settings.inventory-rules.in-production-ca", "Floor products in production in Canada")}
        hint={t(
          "settings.inventory-rules.in-production.hint",
          "Also floor available single items with an in-production quantity: the stock is on its way.",
        )}
      >
        <Switch
          checked={settings.is_floor_in_production_ca}
          disabled={readOnly}
          onChange={(on) => onChange({ is_floor_in_production_ca: on })}
          label={settings.is_floor_in_production_ca ? t("common.on", "On") : t("common.off", "Off")}
        />
      </Row>
      <Row
        label={t("settings.inventory-rules.top-us", "Floored in the US")}
        error={errors["inventory.floor_top_us"]}
      >
        <NumberInput
          value={settings.floor_top_us}
          unit={t("settings.inventory-rules.top.unit", "top products")}
          readOnly={readOnly}
          invalid={Boolean(errors["inventory.floor_top_us"])}
          onChange={(value) => onChange({ floor_top_us: value ?? Number.NaN })}
        />
      </Row>
      <Row label={t("settings.inventory-rules.new-us", "New products in the US")}>
        <Switch
          checked={settings.is_floor_new_us}
          disabled={readOnly}
          onChange={(on) => onChange({ is_floor_new_us: on })}
          label={settings.is_floor_new_us ? t("common.on", "On") : t("common.off", "Off")}
        />
      </Row>
      <Row label={t("settings.inventory-rules.in-production-us", "Floor products in production in the US")}>
        <Switch
          checked={settings.is_floor_in_production_us}
          disabled={readOnly}
          onChange={(on) => onChange({ is_floor_in_production_us: on })}
          label={settings.is_floor_in_production_us ? t("common.on", "On") : t("common.off", "Off")}
        />
      </Row>
      <Row
        label={t("settings.inventory-rules.last-run", "Last run")}
        detail={
          run ? (
            <div className="mt-1 flex flex-col gap-0.5 text-[12px]">
              <span className="text-ink-muted">
                {new Date(run.started_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                {" · "}
                {run.source === "KPI"
                  ? t("settings.inventory-rules.source.kpi", "after the KPI recompute")
                  : t("settings.inventory-rules.source.manual", "applied by hand")}
              </span>
              <span className={run.is_successful ? "text-ink" : "text-risk"}>
                {run.is_successful
                  ? countsSummary(t, run)
                  : t("settings.inventory-rules.failed", "Failed: {{error}}", { error: run.error ?? "" })}
              </span>
            </div>
          ) : (
            <p className="mt-0.5 text-[12px] text-ink-muted">{t("settings.inventory-rules.never", "Never run")}</p>
          )
        }
      >
        {!readOnly && (
          <Button
            size="sm"
            variant="secondary"
            disabled={dirty}
            title={dirty ? t("settings.inventory-rules.save-first", "Save your changes first") : undefined}
            onClick={() => setJobOpen(true)}
          >
            {t("settings.inventory-rules.preview", "Preview and apply")}
          </Button>
        )}
      </Row>
      <InventoryRuleJobDialog open={jobOpen} onClose={() => setJobOpen(false)} />
    </Group>
  );
}

function validate(draft: Draft, t: (key: string, fallback: string, options?: Record<string, unknown>) => string): Errors {
  const errors: Errors = {};
  const required = t("settings.error.required", "Enter a number.");
  const atLeast = (minimum: number) => t("settings.error.at-least", "Must be at least {{minimum}}.", { minimum });
  const check = (key: string, value: number, minimum: number, integer = true, maximum?: number) => {
    if (!Number.isFinite(value)) errors[key] = required;
    else if (value < minimum) errors[key] = atLeast(minimum);
    else if (integer && !Number.isInteger(value)) errors[key] = t("settings.error.whole", "Must be a whole number.");
    else if (maximum !== undefined && value > maximum)
      errors[key] = t("config.error.at-most", "Must be at most {{maximum}}.", { maximum });
  };

  const g = draft.general;
  const share = g.velocity_four_week_share * 100;
  if (!Number.isFinite(share)) errors["general.velocity_four_week_share"] = required;
  else if (share < 0 || share > 100 || !Number.isInteger(Math.round(share * 1e6) / 1e6))
    errors["general.velocity_four_week_share"] = t("settings.error.percent", "A whole percentage between 0 and 100.");
  check("general.velocity_blend_after_weeks", g.velocity_blend_after_weeks, 1);
  check("general.new_product_days", g.new_product_days, 0);

  const pr = draft.product;
  check("product.discontinuation_delay_days", pr.discontinuation_delay_days, 0);
  check("product.discontinuation_single_item_threshold", pr.discontinuation_single_item_threshold, 0, false);
  check("product.discontinuation_bundle_threshold", pr.discontinuation_bundle_threshold, 0, false);

  const inv = draft.inventory;
  check("inventory.floor_top_ca", inv.floor_top_ca, 0);
  check("inventory.floor_top_us", inv.floor_top_us, 0);

  const p = draft.production;
  check("production.min_lot_size", p.min_lot_size, 1);
  check("production.max_lot_size", p.max_lot_size, 1);
  if (!errors["production.max_lot_size"] && !errors["production.min_lot_size"] && p.max_lot_size < p.min_lot_size)
    errors["production.max_lot_size"] = t("settings.error.max-lot", "Cannot be below the minimum lot size.");
  check("production.first_batch_lot_size", p.first_batch_lot_size, 1);
  check("production.weight_urgency", p.weight_urgency, 0, false);
  check("production.weight_performance", p.weight_performance, 0, false);
  check("production.paint_deficit_adjustment_in_weeks", p.paint_deficit_adjustment_in_weeks, 0);
  check("production.new_product_safety_stock", p.new_product_safety_stock, 0);
  check("production.transfer_cover_weeks", p.transfer_cover_weeks, 1);
  check("production.kanban_lead_time_days", p.kanban_lead_time_days, 0);
  check("production.kanban_cycle_days", p.kanban_cycle_days, 1);
  check("production.assembly_split_window_hours", p.assembly_split_window_hours, 0);
  check("production.transfer_reserve_weeks", p.transfer_reserve_weeks, 0);
  check("production.transfer_minimum_reserve_units", p.transfer_minimum_reserve_units, 0);
  check("production.transfer_rounding_units", p.transfer_rounding_units, 1);
  check("production.transfer_seed_units", p.transfer_seed_units, 0);

  const pu = draft.purchasing;
  if (pu) {
    // The purchasing figures are SMALLINT columns: past this the database refuses with a bare error.
    const SMALLINT = 32767;
    if (!Number.isFinite(pu.approval_threshold_amount)) errors["purchasing.approval_threshold_amount"] = required;
    else if (pu.approval_threshold_amount < 0) errors["purchasing.approval_threshold_amount"] = atLeast(0);
    else if (pu.approval_threshold_amount >= 1e10)
      errors["purchasing.approval_threshold_amount"] = t("config.error.at-most", "Must be at most {{maximum}}.", { maximum: 9999999999 });
    check("purchasing.usage_window_days", pu.usage_window_days, 7, true, SMALLINT);
    check("purchasing.default_lead_time_days", pu.default_lead_time_days, 0, true, SMALLINT);
    check("purchasing.default_order_cycle_days", pu.default_order_cycle_days, 1, true, SMALLINT);
    check("purchasing.default_payment_terms_days", pu.default_payment_terms_days, 0, true, SMALLINT);
    check("purchasing.measured_lead_time_minimum_receipts", pu.measured_lead_time_minimum_receipts, 1, true, SMALLINT);
    check("purchasing.measured_lead_time_window_days", pu.measured_lead_time_window_days, 30, true, SMALLINT);
    check("purchasing.lead_time_drift_percent", pu.lead_time_drift_percent, 0, true, SMALLINT);
    check("purchasing.hold_alert_days", pu.hold_alert_days, 1, true, SMALLINT);
    check("purchasing.stale_order_days", pu.stale_order_days, 30, true, SMALLINT);
    check("purchasing.cash_projection_weeks", pu.cash_projection_weeks, 4, true, SMALLINT);
    if (!errors["purchasing.cash_projection_weeks"] && pu.cash_projection_weeks > 52)
      errors["purchasing.cash_projection_weeks"] = t("settings.error.cash-weeks", "At most 52 weeks.");
    check("purchasing.review_window_days", pu.review_window_days, 7, true, SMALLINT);
  }

  const sf = draft.shopFloor;
  if (sf) {
    // The same ranges the table's check enforces, said on the field.
    check("shopFloor.board_refresh_seconds", sf.board_refresh_seconds, 10);
    check("shopFloor.operator_idle_minutes", sf.operator_idle_minutes, 1);
    check("shopFloor.stalled_after_hours", sf.stalled_after_hours, 1);
    check("shopFloor.backflush_interval_minutes", sf.backflush_interval_minutes, 1);
    check("shopFloor.service_label_extra", sf.service_label_extra, 0);
    check("shopFloor.production_sheet_copies", sf.production_sheet_copies, 1);
    check("shopFloor.production_sheet_painted_copies", sf.production_sheet_painted_copies, 1);
    check("shopFloor.long_pallet_threshold_mm", sf.long_pallet_threshold_mm, 1, false);
    check("shopFloor.cutlist_default_quantity", sf.cutlist_default_quantity, 1);
    check("shopFloor.packing_line_speed", sf.packing_line_speed, 1, false);
    check("shopFloor.packing_box_spacing", sf.packing_box_spacing, 0, false);
  }

  const pc = draft.pricing;
  check("pricing.gross_margin_percent", pc.gross_margin_percent, 0, false);
  if (!errors["pricing.gross_margin_percent"] && pc.gross_margin_percent >= 100)
    errors["pricing.gross_margin_percent"] = t("settings.error.margin", "Must be below 100 %.");
  if (!Number.isFinite(pc.minimum_discount_net_margin_percent)) errors["pricing.minimum_discount_net_margin_percent"] = required;
  if (!pc.reference_period_start_date || !pc.reference_period_end_date)
    errors["pricing.reference_period"] = t("settings.error.dates", "Both dates are needed.");
  else if (pc.reference_period_start_date > pc.reference_period_end_date)
    errors["pricing.reference_period"] = t("settings.error.period", "The start must come before the end.");

  draft.rates.forEach((rate, index) => {
    if (!Number.isFinite(rate.rate) || rate.rate <= 0) errors[`rates.${index}`] = t("settings.error.rate", "Must be above 0.");
  });

  const seen = new Set<number>();
  let open = 0;
  draft.bands.forEach((band, index) => {
    const key = `bands.${index}`;
    const numbers = [band.reorder_weeks, band.target_weeks, band.safety_stock_quantity];
    if (numbers.some((value) => !Number.isFinite(value) || !Number.isInteger(value) || value < 0))
      errors[key] = t("settings.error.band-numbers", "Weeks and safety stock are whole numbers, 0 or more.");
    else if (band.target_weeks < 1) errors[key] = t("settings.error.band-target", "The target is at least 1 week.");
    else if (band.reorder_weeks > band.target_weeks)
      errors[key] = t("settings.error.band-reorder", "The reorder point cannot be past the target.");
    else if (band.rank_to !== null && (!Number.isInteger(band.rank_to) || band.rank_to < 1))
      errors[key] = t("settings.error.band-rank", "The rank is a whole number, 1 or more.");
    else if (band.rank_to !== null && seen.has(band.rank_to))
      errors[key] = t("settings.error.band-duplicate", "Another band already ends at this rank.");
    if (band.rank_to === null) open += 1;
    else seen.add(band.rank_to);
  });
  if (open !== 1)
    errors.bands = t("settings.error.band-open", "Exactly one band has no upper rank: it takes every product ranked below the others, and the unranked ones.");

  return errors;
}

interface Changes {
  rows: Array<{ path: string; row: { id: number } }>;
  bandsCreated: RankBandDTO[];
  bandsUpdated: RankBandDTO[];
  bandsDeleted: number[];
  rates: CurrencyRateDTO[];
}

function diff(saved: Draft, draft: Draft): Changes {
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const rows: Changes["rows"] = [];
  if (!same(saved.general, draft.general)) rows.push({ path: SETTINGS_PATHS.general, row: draft.general });
  if (!same(saved.product, draft.product)) rows.push({ path: SETTINGS_PATHS.product, row: draft.product });
  if (!same(saved.inventory, draft.inventory)) rows.push({ path: SETTINGS_PATHS.inventory, row: draft.inventory });
  if (!same(saved.production, draft.production)) rows.push({ path: SETTINGS_PATHS.production, row: draft.production });
  if (draft.purchasing && !same(saved.purchasing, draft.purchasing)) rows.push({ path: SETTINGS_PATHS.purchasing, row: draft.purchasing });
  if (draft.shopFloor && !same(saved.shopFloor, draft.shopFloor)) rows.push({ path: SETTINGS_PATHS.shopFloor, row: draft.shopFloor });
  if (!same(saved.pricing, draft.pricing)) rows.push({ path: SETTINGS_PATHS.pricing, row: draft.pricing });

  const before = new Map(saved.bands.map((band) => [band.id, band]));
  const kept = new Set(draft.bands.map((band) => band.id));
  return {
    rows,
    bandsCreated: draft.bands.filter((band) => band.id < 0),
    bandsUpdated: draft.bands.filter((band) => band.id > 0 && !same(before.get(band.id), band)),
    bandsDeleted: saved.bands.filter((band) => !kept.has(band.id)).map((band) => band.id),
    rates: draft.rates.filter((rate, index) => !same(saved.rates[index], rate)),
  };
}
